import React, { useEffect, useState } from 'react';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import './RLContact.css';

const sampleReviews = [
  {
    name: 'Quant Ops Team',
    role: 'Internal Users',
    message: 'Interface feels consistent across Training, Competition, and Portfolio. Fast to jump between cards.',
    rating: 5,
    date: 'Today',
  },
  {
    name: 'Risk Committee',
    role: 'Stakeholder',
    message: 'Appreciate quick summaries and deploy buttons staying visible without extra chrome.',
    rating: 4,
    date: 'This week',
  },
  {
    name: 'Support',
    role: 'Feedback Loop',
    message: 'Keep shipping UI polish — the new layouts make reviews much faster.',
    rating: 5,
    date: 'This month',
  },
];

const TikTokIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M21 9.5a6.5 6.5 0 0 1-4-1.38V15a5.5 5.5 0 1 1-6.5-5.39V12a3.5 3.5 0 1 0 2.5 3.36V3h3a3.5 3.5 0 0 0 3.5 3.5H21v3z" />
  </svg>
);

function RLContact() {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '', rating: 5 });
  const [showDemoDetails, setShowDemoDetails] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setReviews(sampleReviews);
      setLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      setStatus({ type: 'error', text: 'Please add a message before sending.' });
      return;
    }
    setReviews((prev) => [
      { name: form.name || 'Anonymous', role: form.email || 'User', message: form.message, rating: form.rating, date: 'Now' },
      ...prev,
    ]);
    setStatus({ type: 'success', text: 'Thanks! We received your note.' });
    setForm({ name: '', email: '', message: '', rating: 5 });
  };

  const handleBookDemo = () => {
    setShowDemoDetails(false);
    window.location.href = '/demo';
  };

  return (
    <div className="rl-contact">
      <div className="rl-contact-grid">
        <div className="rl-card">
          <h3>📝 Share Feedback</h3>
          <form className="rl-feedback-form" onSubmit={handleSubmit}>
            <div className="rl-field">
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="What's your name?"
              />
            </div>
            <div className="rl-field">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="What's your email?"
              />
            </div>
            <div className="rl-field">
              <label>Message</label>
              <textarea
                value={form.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="What should we improve?"
                rows={4}
              />
            </div>
            <div className="rl-field">
              <label>Rating</label>
              <div className="rl-star-row">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    className={`rl-star ${form.rating >= star ? 'filled' : ''}`}
                    onClick={() => handleChange('rating', star)}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            {status && (
              <div className={`rl-status-banner ${status.type}`}>
                {status.type === 'success' ? '✅' : '⚠️'} {status.text}
              </div>
            )}
            <button type="submit" className="rl-btn rl-btn-primary">
              📤 Send feedback
            </button>
          </form>
        </div>

        <div className="rl-card">
          <h3>⭐ Reviews & Kudos</h3>
          {loading ? (
            <div className="rl-reviews">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="rl-review skeleton">
                  <div className="skeleton-line short" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line" />
                </div>
              ))}
            </div>
          ) : (
            <div className="rl-reviews">
              {reviews.map((rev, idx) => (
                <div key={idx} className="rl-review">
                  <div className="rl-review-header">
                    <div>
                      <div className="rl-review-name">{rev.name}</div>
                      <div className="rl-review-role">{rev.role}</div>
                    </div>
                    <div className="rl-review-meta">
                      <span className="rl-rating">{'★'.repeat(rev.rating)}</span>
                      <span className="rl-review-date">{rev.date}</span>
                    </div>
                  </div>
                  <p className="rl-review-message">{rev.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rl-card">
          <h3>📮 Contact & Support</h3>
          <div className="rl-contact-body">
            <div className="rl-contact-row">
              <span className="rl-label">Slack</span>
              <span className="rl-value">#quant-terminal</span>
            </div>
            <div className="rl-contact-row">
              <span className="rl-label">Email</span>
              <span className="rl-value">support@futureai.local</span>
            </div>
            <div className="rl-contact-row">
              <span className="rl-label">Hours</span>
              <span className="rl-value">Mon–Fri, 9:00–18:00 UTC</span>
            </div>
            <div className="rl-contact-row">
              <span className="rl-label">SLA</span>
              <span className="rl-value">P1: 2h • P2: 8h • P3: 24h</span>
            </div>
            <div className="rl-contact-row">
              <span className="rl-label">Docs</span>
              <span className="rl-value">
                <a href="#/docs" className="rl-link">Open handbook</a>
              </span>
            </div>
          </div>
        </div>

        <div className="rl-card">
          <h3>📅 Book a Demo</h3>
          <p className="rl-note">
            See your symbols in a 1-week pilot, compare agents, and ship a deployable model fast.
          </p>
          <ul className="rl-list">
            <li>1-week pilot on your symbols & timeframes</li>
            <li>Deploy best-performing agent in one click</li>
            <li>Enterprise controls: audit, roles, circuit breakers</li>
          </ul>
          <div className="rl-actions center">
            <button
              type="button"
              className="rl-btn rl-btn-secondary"
              onClick={() => setShowDemoDetails(true)}
            >
              View demo details
            </button>
            <button className="rl-btn rl-btn-primary" type="button" onClick={handleBookDemo}>
              🚀 Book now
            </button>
          </div>
        </div>

        <div className="rl-card">
          <h3>📣 Follow Us</h3>
          <p className="rl-note">Release notes, trading insights, and live feature drops.</p>
          <div className="rl-actions socials">
            <a className="rl-btn rl-btn-secondary" href="https://twitter.com" target="_blank" rel="noreferrer">
              <TwitterIcon fontSize="small" sx={{ mr: 0.5 }} />
              X / Twitter
            </a>
            <a className="rl-btn rl-btn-secondary" href="https://instagram.com" target="_blank" rel="noreferrer">
              <InstagramIcon fontSize="small" sx={{ mr: 0.5 }} />
              Instagram
            </a>
            <a className="rl-btn rl-btn-secondary" href="https://tiktok.com" target="_blank" rel="noreferrer">
              <TikTokIcon />
              <span style={{ marginLeft: 6 }}>TikTok</span>
            </a>
          </div>
        </div>

        <div className="rl-card">
          <h3>🔗 Quick Links</h3>
          <div className="rl-links-grid">
            <a className="rl-link-tile" href="#/training">🎯 Training</a>
            <a className="rl-link-tile" href="#/competition">🏆 Competition</a>
            <a className="rl-link-tile" href="#/models">🤖 Models</a>
            <a className="rl-link-tile" href="#/portfolio">📊 Portfolio</a>
          </div>
          <p className="rl-note">
            Need a feature review? Drop a note and we will align it with the next sprint.
          </p>
        </div>
      </div>

      {showDemoDetails && (
        <div className="demo-modal-overlay" onClick={() => setShowDemoDetails(false)}>
          <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="demo-modal-header">
              <h4>Launch your best RL agent in 7 days</h4>
              <button className="demo-modal-close" onClick={() => setShowDemoDetails(false)}>×</button>
            </div>
            <div className="demo-modal-body">
              <p className="rl-note" style={{ marginBottom: '0.5rem', fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}>
                Book now to ship a production-ready agent in days, not months—live on <em>your</em> symbols and guardrails.
              </p>
              <ul>
                <li>Scope: your symbols/timeframes with live training, competition, and portfolio walk-through.</li>
                <li>Outcome: best-performing agent deployed in one click, with handoff and next steps.</li>
                <li>Controls: audit trail, roles, circuit breakers, fallback strategies (enterprise-ready).</li>
                <li>Prep: share symbols, KPIs (Sharpe/DD/win rate/latency), and data access mode to fast-track.</li>
                <li>Availability: limited demo slots weekly—lock your time to start a 1-week pilot.</li>
              </ul>
              <div className="demo-modal-actions">
                <button className="rl-btn rl-btn-primary" type="button" onClick={handleBookDemo}>🚀 Book now</button>
                <button className="rl-btn rl-btn-secondary" onClick={() => setShowDemoDetails(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RLContact;

